import React from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import RoomSlugRow from './RoomSlugRow';
import AddRoomSlugForm from './AddRoomSlugForm';
import {useListRoomSlugs} from '../../../../../service/hooks/useRoomSlug';
import {RequestParam} from '../../../../../incase-domain/requestParams';
import '../../../PresEditPage/components/settings/SlugSettings/slugSettings.scss';

interface EditRoomShortLinksTabProps {
    roomId: string;
    presenterLinkCode?: string;
    requestParams: RequestParam[];
}

function EditRoomShortLinksTab({roomId, presenterLinkCode, requestParams}: EditRoomShortLinksTabProps): JSX.Element {
    const {t} = useTranslation();
    const {data: slugs, isLoading} = useListRoomSlugs(roomId);

    const viewerSlugs = (slugs ?? []).filter((slug) => !slug.presenterCode);
    const presenterSlugs = (slugs ?? []).filter((slug) => !!slug.presenterCode);
    const requiredParams = requestParams.filter((p) => p.required);
    const adminParams = requestParams.map((p) => ({key: p.key, value: 'ADMIN'}));

    return (
        <div className="u-p-m c-slugSettings">
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{t('roomsPage.settings.shortLink.title')}</div>
            </div>

            <p className="u-text-gray-500 u-mb-4">{t('roomsPage.settings.shortLink.description')}</p>

            {requiredParams.length > 0 && (
                <div className="c-slugSettings__warning">
                    <Icon icon="solar:danger-triangle-linear" width={16} />
                    <span>
                        {t('roomsPage.settings.shortLink.requiredParamsWarning', {
                            params: requiredParams.map((p) => p.key).join(', '),
                        })}
                    </span>
                </div>
            )}

            {isLoading && (
                <div className="u-flex u-v-center u-gap-2 c-slugSettings__loading">
                    <Icon icon="solar:refresh-linear" width={16} />
                </div>
            )}

            {!isLoading && (
                <div className="u-flex u-flex-col u-gap-5">
                    <div className="c-slugSettings__group">
                        <span className="c-slugSettings__groupTitle">{t('roomsPage.settings.shortLink.viewerLinks')}</span>
                        {viewerSlugs.length === 0 && (
                            <p className="c-slugSettings__empty">{t('roomsPage.settings.shortLink.noViewerSlug')}</p>
                        )}
                        {viewerSlugs.map((slug) => (
                            <RoomSlugRow key={slug._id} slug={slug} roomId={roomId} requestParams={requestParams} />
                        ))}
                        <AddRoomSlugForm
                            roomId={roomId}
                            addLabel={t('roomsPage.settings.shortLink.addViewerSlug')}
                            requestParams={requestParams}
                        />
                    </div>

                    <div className="c-slugSettings__group">
                        <span className="c-slugSettings__groupTitle">{t('roomsPage.settings.shortLink.presenterLinks')}</span>
                        {presenterSlugs.length === 0 && (
                            <p className="c-slugSettings__empty">{t('roomsPage.settings.shortLink.noPresenterSlug')}</p>
                        )}
                        {presenterSlugs.map((slug) => (
                            <RoomSlugRow key={slug._id} slug={slug} roomId={roomId} requestParams={requestParams} showParams={false} />
                        ))}
                        {presenterLinkCode ? (
                            <AddRoomSlugForm
                                roomId={roomId}
                                presenterCode={presenterLinkCode}
                                addLabel={t('roomsPage.settings.shortLink.addPresenterSlug')}
                                requestParams={requestParams}
                                showParams={false}
                                defaultParams={adminParams}
                            />
                        ) : (
                            <p className="c-slugSettings__empty">{t('roomsPage.settings.shortLink.presenterCodeMissing')}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default EditRoomShortLinksTab;
